import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DisplayProofForm from "../components/SalesExecutive/DisplayProofForm";
import { getEligibleDisplayProofVisits } from "../services/api";

/**
 * Display proof submission wrapper.
 *
 * Mirrors RecordSale: the user picks an eligible visit (active, or checked out
 * within the last 24 hours) before the form is shown. An active check-in is not
 * required, so display proof can still be submitted after checkout.
 */
export default function RecordDisplayProof({ outlet: outletProp, onCancel, onSave }) {
  const { t } = useTranslation();

  const [eligibleVisits, setEligibleVisits] = useState([]);
  const [selectedVisitId, setSelectedVisitId] = useState("");
  const [loadingVisits, setLoadingVisits] = useState(true);
  const [isChoosingVisit, setIsChoosingVisit] = useState(false);
  const formStartRef = useRef(null);

  useEffect(() => {
    if (outletProp?.check_in_id) {
      setEligibleVisits([outletProp]);
      setSelectedVisitId(String(outletProp.check_in_id));
      setLoadingVisits(false);
      return undefined;
    }

    let isMounted = true;

    const loadEligibleVisits = async () => {
      try {
        setLoadingVisits(true);
        const result = await getEligibleDisplayProofVisits({ hours: 24 });
        if (!isMounted) return;

        const visits = Array.isArray(result?.data) ? result.data : [];
        setEligibleVisits(visits);
        setSelectedVisitId("");
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to fetch eligible display proof visits:", error);
        setEligibleVisits([]);
        setSelectedVisitId("");
      } finally {
        if (isMounted) {
          setLoadingVisits(false);
        }
      }
    };

    loadEligibleVisits();

    return () => {
      isMounted = false;
    };
  }, [outletProp]);

  const displayVisits = useMemo(() => {
    const visitMap = new Map();

    eligibleVisits.forEach((visit) => {
      const outletKey = String(
        visit.outlet_id ||
          visit.bp_outlet_id ||
          `${visit.outlet_name || ""}|${visit.outlet_address || ""}`,
      );
      const existingVisit = visitMap.get(outletKey);

      if (!existingVisit) {
        visitMap.set(outletKey, visit);
        return;
      }

      const existingPriority = existingVisit.is_active ? 2 : 1;
      const currentPriority = visit.is_active ? 2 : 1;
      const existingTime = new Date(
        existingVisit.checked_out_at || existingVisit.checked_in_at || 0,
      ).getTime();
      const currentTime = new Date(
        visit.checked_out_at || visit.checked_in_at || 0,
      ).getTime();

      if (
        currentPriority > existingPriority ||
        (currentPriority === existingPriority && currentTime > existingTime)
      ) {
        visitMap.set(outletKey, visit);
      }
    });

    return Array.from(visitMap.values());
  }, [eligibleVisits]);

  useEffect(() => {
    if (outletProp?.check_in_id) {
      setIsChoosingVisit(false);
      return;
    }

    if (loadingVisits) return;

    if (
      selectedVisitId &&
      displayVisits.some(
        (visit) => String(visit.check_in_id) === String(selectedVisitId),
      )
    ) {
      return;
    }

    const activeVisit = displayVisits.find((visit) => visit.is_active);
    if (activeVisit) {
      setSelectedVisitId(String(activeVisit.check_in_id));
      setIsChoosingVisit(false);
      return;
    }

    if (displayVisits.length === 1) {
      setSelectedVisitId(String(displayVisits[0].check_in_id));
      setIsChoosingVisit(false);
      return;
    }

    setSelectedVisitId("");
    setIsChoosingVisit(displayVisits.length > 1);
  }, [displayVisits, loadingVisits, outletProp, selectedVisitId]);

  const activeCheckIn = useMemo(() => {
    if (outletProp?.check_in_id) return outletProp;

    return (
      displayVisits.find(
        (visit) => String(visit.check_in_id) === String(selectedVisitId),
      ) || null
    );
  }, [displayVisits, outletProp, selectedVisitId]);

  useEffect(() => {
    if (!activeCheckIn || isChoosingVisit) return;

    const timer = window.setTimeout(() => {
      formStartRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [activeCheckIn, isChoosingVisit]);

  const renderVisitSelector = () => (
    <div className="se-sales-visit-card">
      <div className="se-sales-visit-card-header">
        <h3>
          {t("se.displayProof.visitSelectorTitle", {
            defaultValue: "Select Outlet for Display Proof",
          })}
        </h3>
        <p>
          {t("se.displayProof.visitSelectorSubtitle", {
            defaultValue:
              "Choose the outlet from your latest active or recent visit, then submit the display proof there.",
          })}
        </p>
      </div>

      {loadingVisits ? (
        <div className="se-sales-visit-skeleton" aria-hidden="true">
          <div className="se-sales-visit-skeleton-line se-sales-visit-skeleton-line-lg"></div>
          <div className="se-sales-visit-skeleton-line"></div>
          <div className="se-sales-visit-skeleton-line se-sales-visit-skeleton-line-sm"></div>
        </div>
      ) : displayVisits.length === 0 ? null : (
        <div className="se-sales-visit-options">
          {displayVisits.map((visit) => {
            const isSelected =
              String(visit.check_in_id) === String(selectedVisitId);

            return (
              <button
                key={visit.check_in_id}
                type="button"
                className={`se-sales-visit-option ${isSelected ? "selected" : ""}`}
                onClick={() => {
                  setSelectedVisitId(String(visit.check_in_id));
                  setIsChoosingVisit(false);
                }}
              >
                <div className="se-sales-visit-option-top">
                  <span className="se-sales-visit-option-name">
                    {visit.outlet_name}
                  </span>
                  <span
                    className={`se-sales-visit-status ${
                      visit.is_active ? "active" : "completed"
                    }`}
                  >
                    {visit.is_active
                      ? t("se.sales.visitActive")
                      : t("se.sales.visitCompleted")}
                  </span>
                </div>
                <div className="se-sales-visit-option-address">
                  {visit.outlet_address || t("se.sales.noAddress")}
                </div>
                <div className="se-sales-visit-option-time">
                  {visit.is_active
                    ? t("se.sales.checkedInAt", {
                        time: new Date(visit.checked_in_at).toLocaleString(),
                      })
                    : t("se.sales.checkedOutAt", {
                        time: new Date(visit.checked_out_at).toLocaleString(),
                      })}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="se-sales-entry-view">
      {!outletProp?.check_in_id &&
      (loadingVisits ||
        isChoosingVisit ||
        (!activeCheckIn && displayVisits.length > 0))
        ? renderVisitSelector()
        : null}

      {activeCheckIn && !isChoosingVisit && (
        <div className="se-sales-selected-visit">
          <div className="se-sales-selected-visit-top">
            <div>
              <div className="se-sales-selected-visit-label">
                {t("se.sales.selectedVisit")}
              </div>
              <div className="se-sales-selected-visit-name">
                {activeCheckIn.outlet_name}
              </div>
            </div>
          </div>
          <div className="se-sales-selected-visit-address">
            {activeCheckIn.outlet_address || t("se.sales.noAddress")}
          </div>
        </div>
      )}

      {activeCheckIn && !isChoosingVisit ? (
        <div ref={formStartRef}>
          <DisplayProofForm
            activeCheckIn={activeCheckIn}
            onCancel={onCancel}
            onSave={onSave}
          />
        </div>
      ) : !loadingVisits && !isChoosingVisit && displayVisits.length === 0 ? (
        <div className="se-empty-tab">
          <div className="se-empty-tab-card">
            <h3>
              {t("se.displayProof.noEligibleVisitsTitle", {
                defaultValue: "No outlet available",
              })}
            </h3>
            <p>
              {t("se.displayProof.noEligibleVisitsMessage", {
                defaultValue:
                  "You need an active visit or a completed visit from the last 24 hours before submitting display proof.",
              })}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
